/**
 * AI Workflow Clone Modal
 *
 * Allows users to clone and edit a successful workflow for re-use.
 * - Clone workflow structure without files/attachments
 * - Edit task description and constraints
 * - Create new workflow instance ready for execution
 */

import React, { useState, useEffect } from 'react';
import { X, Copy, Sparkles, AlertCircle } from 'lucide-react';

export default function AIWorkflowCloneModal({
  show,
  onClose,
  onClone,
  originalWorkflow,
  cloning = false
}) {
  const [taskDescription, setTaskDescription] = useState('');
  const [domain, setDomain] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [modelPreference, setModelPreference] = useState('balanced');

  // Update form fields when originalWorkflow changes
  useEffect(() => {
    if (originalWorkflow) {
      console.log('Clone modal - Original workflow:', originalWorkflow);
      setTaskDescription(originalWorkflow.task_description || '');
      setDomain(originalWorkflow.domain || '');
      setCompany(originalWorkflow.company || '');
      setRole(originalWorkflow.role || '');
      setModelPreference(originalWorkflow.constraints?.model_preference || 'balanced');
    }
  }, [originalWorkflow]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const cloneData = {
      task_description: taskDescription,
      domain: domain || undefined,
      company: company || undefined,
      role: role || undefined,
      constraints: {
        model_preference: modelPreference
      }
    };

    onClone(cloneData);
  };

  if (!show || !originalWorkflow) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Copy className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Clone & Edit Workflow
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Create a new workflow based on this template
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Cloning will create a brand new workflow with:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>Similar structure based on your edited task description</li>
                  <li>Fresh execution state (ready to run)</li>
                  <li>No attached files (you can re-attach them later)</li>
                  <li>New workflow ID and independent cost tracking</li>
                </ul>
              </div>
            </div>

            {/* Original Workflow Info */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Original Workflow
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <div>
                  <span className="font-medium">Nodes:</span> {originalWorkflow.total_nodes}
                </div>
                <div>
                  <span className="font-medium">Cost:</span> £
                  {(originalWorkflow.actual_cost || originalWorkflow.estimated_cost || 0).toFixed(4)}
                </div>
                <div>
                  <span className="font-medium">Status:</span>{' '}
                  <span className="capitalize">{originalWorkflow.status}</span>
                </div>
              </div>
            </div>

            {/* Task Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task Description *
              </label>
              <textarea
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={4}
                placeholder="Describe what you want to accomplish..."
                required
              />
              <p className="text-xs text-gray-500 mt-2">
                Edit the task description to customize the workflow for your new use case
              </p>
            </div>

            {/* Optional Context Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain
                </label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Healthcare, Legal"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Acme Corp"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <input
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Product Manager"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model Preference
                </label>
                <select
                  value={modelPreference}
                  onChange={(e) => setModelPreference(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="cost_optimized">Cost Optimized (40-60% savings)</option>
                  <option value="balanced">Balanced (Default)</option>
                  <option value="quality_first">Quality First (Best models)</option>
                </select>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            Files and attachments are not cloned
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={cloning}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={cloning || !taskDescription.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {cloning ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Cloning...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Clone & Decompose</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
